// GA4 (gtag.js) with Google Consent Mode, plus Microsoft Clarity. Nothing loads until the visitor accepts.
import { clarityEnabled, clarityEvent, loadClarity, revokeClarity } from "@/lib/clarity";

const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
export const CONSENT_KEY = "cy_analytics_consent";
let lastPublicUrl = "";

const PRIVATE_PREFIXES = ["/merchant", "/admin", "/account", "/user", "/dashboard", "/wallet", "/rewards",
  "/reseller", "/affiliate", "/settings", "/r/", "/reset-password", "/verify", "/legal/agreements", "/terms/documents"];

export function gaEnabled() {
  if (!GA_ID || typeof window === "undefined") return false;
  const preview = window.location.hostname.endsWith(".preview.emergentagent.com");
  return preview || process.env.REACT_APP_GA_PRODUCTION_APPROVED === "true";
}

export const analyticsEnabled = () => gaEnabled() || clarityEnabled();

export const getConsent = () => window.localStorage.getItem(CONSENT_KEY);
export const isPublicPath = (path) => !PRIVATE_PREFIXES.some((p) => path === p || path.startsWith(p.endsWith("/") ? p : `${p}/`));

function cleanUrl() {
  const { origin, pathname, search } = window.location;
  const utm = [...new URLSearchParams(search)].filter(([k]) => k.startsWith("utm_") || k === "gclid");
  return origin + pathname + (utm.length ? `?${new URLSearchParams(utm)}` : "");
}

function ensureGtag() {
  if (window.gtag) return;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() { window.dataLayer.push(arguments); };
  window.gtag("consent", "default", { analytics_storage: "denied", ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied" });
}

export function grantAndLoad() {
  loadClarity();
  if (!gaEnabled()) return;
  ensureGtag();
  window.gtag("consent", "update", { analytics_storage: "granted" });
  if (document.getElementById("cy-gtag")) return;
  const s = document.createElement("script");
  s.id = "cy-gtag";
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);
  window.gtag("js", new Date());
  window.gtag("config", GA_ID, { send_page_view: false, anonymize_ip: true });
}

export const OPEN_SETTINGS_EVENT = "cy:open-cookie-settings";
export const openCookieSettings = () => window.dispatchEvent(new Event(OPEN_SETTINGS_EVENT));

function clearGaCookies() {
  const host = window.location.hostname;
  const domains = ["", host, `.${host}`, `.${host.split(".").slice(-2).join(".")}`];
  document.cookie.split(";").map((c) => c.trim().split("=")[0]).filter((n) => n === "_ga" || n.startsWith("_ga_"))
    .forEach((n) => domains.forEach((d) => {
      document.cookie = `${n}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${d ? `; domain=${d}` : ""}`;
    }));
}

export function setConsent(value) {
  window.localStorage.setItem(CONSENT_KEY, value);
  if (value === "granted") {
    window[`ga-disable-${GA_ID}`] = false;
    grantAndLoad();
    return;
  }
  revokeClarity();
  window[`ga-disable-${GA_ID}`] = true;
  if (window.gtag) window.gtag("consent", "update", { analytics_storage: "denied" });
  clearGaCookies();
}

// Conversion events. Callers must never pass names, emails or phone numbers.
export function trackEvent(name, params = {}) {
  if (!gaEnabled() || getConsent() !== "granted" || !window.gtag) return;
  window.gtag("event", name, params);
}

let demoQualifiers = {};
export function trackApiConversion(config, status) {
  if (status < 200 || status >= 300 || (config.method || "").toLowerCase() !== "post") return;
  const url = config.url || "";
  let body = {};
  try { body = typeof config.data === "string" ? JSON.parse(config.data) : config.data || {}; } catch { body = {}; }
  if (url.endsWith("/demo/request")) {
    demoQualifiers = { industry: body.industry || undefined, company_size: body.company_size || undefined, province: body.province || undefined };
  } else if (url.endsWith("/demo/book")) {
    trackEvent("generate_lead", { lead_source: "DEMO_PAGE", ...demoQualifiers });
    clarityEvent("demo_booked");
  } else if (url.endsWith("/auth/register") && body.role === "merchant") {
    trackEvent("sign_up", { method: "email", user_type: "merchant", ...utmParams() });
    clarityEvent("merchant_signup_completed");
  }
}

// First-touch UTM attribution kept for this browser tab (sanitized, no personal data).
export function utmParams() {
  try {
    const saved = JSON.parse(window.sessionStorage.getItem("cy_utm") || "null");
    if (saved) return saved;
    const q = new URLSearchParams(window.location.search);
    const clean = (v) => (v || "").replace(/[^\w.\-+ ]/g, "").slice(0, 80) || undefined;
    const u = { campaign_source: clean(q.get("utm_source")), campaign_medium: clean(q.get("utm_medium")), campaign_name: clean(q.get("utm_campaign")) };
    if (u.campaign_source || u.campaign_medium || u.campaign_name) window.sessionStorage.setItem("cy_utm", JSON.stringify(u));
    return u;
  } catch { return {}; }
}

export function trackPageView(path) {
  if (!gaEnabled() || getConsent() !== "granted" || !window.gtag) return;
  // Official GA opt-out flag: blocks every hit (incl. GA's automatic history page views) on private pages.
  window[`ga-disable-${GA_ID}`] = !isPublicPath(path);
  if (!isPublicPath(path)) return;
  const location = cleanUrl();
  // Referrer is always the last public page (never a private/tokenised URL).
  const referrer = lastPublicUrl || (document.referrer.startsWith(window.location.origin) ? "" : document.referrer.split("?")[0]);
  window.gtag("set", { page_location: location, page_referrer: referrer });
  window.gtag("event", "page_view", { page_location: location, page_path: path, page_title: document.title, page_referrer: referrer });
  lastPublicUrl = location;
}
